import { Component, ErrorInfo, ReactNode } from 'react';

import { ErrorPage } from './ErrorPage';

type Props = {
  children: ReactNode;
};

type State = {
  /** Пойманная ошибка рендера; null — ошибок нет */
  error: Error | null;
  /** Стек React-компонентов на момент ошибки */
  componentStack: string | null;
};

/**
 * Верхнеуровневый перехватчик ошибок рендера.
 *
 * Ловит исключения из дочернего дерева и вместо аварийного «белого экрана» показывает
 * {@link ErrorPage} — страницу восстановления в стиле проекта. Восстановиться из ошибки
 * можно только полной перезагрузкой (её предлагает {@link ErrorPage}), поэтому сбрасывать
 * состояние boundary отдельной кнопкой не нужно.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Логируем в консоль, чтобы ошибка осталась доступной для диагностики
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
  }

  render(): ReactNode {
    const { error, componentStack } = this.state;

    if (error) {
      return <ErrorPage error={error} componentStack={componentStack} />;
    }

    return this.props.children;
  }
}
